/* eslint-disable jsx-a11y/no-static-element-interactions */
import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import { requestSelectors } from 'src/store/requests/selectors';
import { getAvailableComplexes } from 'src/store/crosstable/selectors';
import { CRMNames } from 'src/typings/app';
import { getCrmData } from 'src/store/app/selectors';
import { translations } from 'src/constants/translations';
import { useSetDocumentTitle } from 'src/hooks';
import { IComplex } from 'src/typings/complexes';
import { SearchField } from 'src/components/SearchField';
import { TypeOfRole } from 'src/typings/user';
import { Loader } from 'src/components/Loader';
import { AppPagination } from 'src/components/AppPagination';
import { usePagination } from 'src/hooks/usePagination';
import { AvailabilityReportModal } from 'src/components/profileComponents/Complexes/components/AvailabilityReportModal';
import { toQueryString } from 'src/helpers/buildQueryParamsString';
import { useGetComplexes } from 'src/hooks/useGetComplexes';
import { complexRequestProperties } from 'src/components/profileComponents/Complexes/types';
import { useAppMediaQuery } from 'src/hooks/app/useAppMediaQuery';
import { ComplexItem } from './components/ComplexItem';
import { useStyles } from './styles';
import { ButtonApp } from '../../Button';
import { ComplexModal } from './components/ComplexModal';

export const Complexes = () => {
  const styles = useStyles({});
  const crmData = useSelector(getCrmData);
  const user = useSelector(requestSelectors.user.getUserData);
  const availableComplexes = useSelector(getAvailableComplexes);
  const [page, setPage] = useState<number>(1);
  const [search, setSearch] = useState('');
  const {
    data,
    isLoading,
  } = useGetComplexes(!crmData ? user?.id : undefined, page, toQueryString({
    ...complexRequestProperties,
    users: {
      id: user?.id,
      email: crmData?.userEmail,
    },
    bitrixCategories: crmData?.categoryId,
    developers: {
      bitrixProject: {
        domain: crmData?.domain,
      },
    },
    batchSearch: search.toLowerCase(),
  }));
  const { members, totalItems, itemsPerPage } = data || {};
  const { is } = useAppMediaQuery();
  const isLargeScreen = is('(min-width:1200px)');

  const [modalOpen, setModalOpen] = useState(false);
  const [availabilityModalOpen, setAvailabilityModalOpen] = useState(false);
  const [editedComplex, setEditedComplex] = useState<IComplex|undefined>();
  const newProjectButtonAccess = [TypeOfRole.superadmin, TypeOfRole.admin];
  const exportAvailabilityButtonAccess = [TypeOfRole.superadmin, TypeOfRole.supervisor];

  const handleAddComplex = () => {
    setEditedComplex(undefined);
    setModalOpen(true);
  };

  const [filteredComplexes, setFilteredComplexes] = useState<IComplex[] | null>(null);
  const { totalPages } = usePagination<IComplex>({ list: availableComplexes || members || [], totalItems: availableComplexes?.length || totalItems, itemsPerPage });

  useSetDocumentTitle(translations.complexesLink);

  useEffect(() => {
    if (crmData?.crm === CRMNames.bitrix || crmData?.crm === CRMNames.creatio) {
      setFilteredComplexes(availableComplexes || null);
    } else {
      setFilteredComplexes(availableComplexes || members || null);
    }
  }, [crmData, availableComplexes, members]);

  const handleChangeComplexes = async (searchTerm: string) => {
    setSearch(searchTerm);
    setPage(1);
  };

  return (
    <Box py={8} px={4} height="100%" className={styles.wrapper}>
      <Box mb={8} display="flex" flexWrap="nowrap">
        <Box fontSize="32px" mr="24px" fontWeight={700}>
          {translations.projectsLink}
        </Box>
        <Box width={340} mr="auto">
          <SearchField
            handleChange={handleChangeComplexes}
            placeholder={`${translations.project} ${translations.or} ${translations.developer}`}
          />
        </Box>
        {!crmData && user?.role && newProjectButtonAccess.includes(user?.role?.name) && (
          <Box ml="24px">
            <ButtonApp
              text={translations.createProject}
              onClick={handleAddComplex}
              color="primary"
              variant="contained"
              type="submit"
              className={styles.btn}
            />
          </Box>
        )}
        {!crmData && user?.role && exportAvailabilityButtonAccess.includes(user.role.name) && (
          <Box ml="24px">
            <ButtonApp
              text={translations.exportAvailability}
              onClick={() => setAvailabilityModalOpen(true)}
              color="primary"
              variant="contained"
              type="submit"
              className={styles.btn}
            />
          </Box>
        )}
      </Box>
      <Box>
        {filteredComplexes && !!filteredComplexes.length && (!isLoading || filteredComplexes?.length) && (
          <>
            <Grid container spacing={6} pb="48px">
              {filteredComplexes.map((complex: IComplex) => {
                return (
                  <Grid item xs={isLargeScreen ? 3 : 4} key={`complex${complex.id}`}>
                    <ComplexItem complex={complex} />
                  </Grid>
                );
              })}
            </Grid>
          </>
        )}
        {(isLoading && !filteredComplexes?.length) && (
          <Box
            width="100%"
            height="100%"
            display="flex"
            alignItems="center"
            justifyContent="center"
            pb={8}
          >
            <Loader />
          </Box>
        )}
        {totalPages > 1 && (
        <Box display="flex" justifyContent="center" pb={8} hidden={isLoading || !filteredComplexes?.length}>
          <AppPagination
            count={totalPages}
            page={page}
            onChange={(_, nextPage) => setPage(nextPage)}
          />
        </Box>
        ) }
        { filteredComplexes && !filteredComplexes.length && !isLoading && (
          <Box display="flex" justifyContent="center">
            {translations.notFoundComplexes}
          </Box>
        )}
      </Box>
      {availabilityModalOpen && (
        <AvailabilityReportModal
          isOpen={availabilityModalOpen}
          setModalOpen={setAvailabilityModalOpen}
        />
      )}
      {modalOpen && (
        <ComplexModal
          isOpen={modalOpen}
          handleChangeOpen={setModalOpen}
          refreshComplexes={() => {}}
          complex={editedComplex}
        />
      )}
    </Box>
  );
};
